import { useTranslation } from '@crew/modules/i18n'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { memo, useCallback } from 'react'
import { useTaskDetailRelatedInfoListContext } from '../../taskDetailRelatedInfoList'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { useTaskDetailRelatedInfoListToolbar } from './useTaskDetailRelatedInfoListToolbar'

export const TaskDetailRelatedInfoListToolbar = memo(() => {
  const { t } = useTranslation()
  const [showApiErrors] = useShowApiErrors()
  const { success } = useToast()

  const {
    insertEntityReferences,
    isLoadingInsertEntityReferencesMutation,
    generateTaskRelatedInfo,
    isLoadingGenerateRelatedMutation,
  } = useTaskDetailRelatedInfoListToolbar()

  const {
    displayState,
    setDisplayState,
    setRelatedSuggestions,
    setSelectedSuggestions,
    selectedSuggestions,
  } = useTaskDetailRelatedInfoListContext()

  // 表示制御
  const showRelatedInfoTable = displayState === 'relatedInfo'
  const showSearching = displayState === 'searching'
  const showSuggestionTable = displayState === 'suggestion'

  // 「AIで関連情報を検索する」ボタンをクリックしたときの処理
  const handleFindRelatedInfoButtonClick = useCallback(async () => {
    try {
      // 生成APIを呼び出し
      await generateTaskRelatedInfo()
      // 表示を検索中に切り替え
      setDisplayState('searching')
    } catch (err) {
      showApiErrors(err)
    }
  }, [generateTaskRelatedInfo, setDisplayState, showApiErrors])

  // 「関連情報を追加」ボタンをクリックしたときの処理
  const handleAddRelatedInfoButtonClick = useCallback(async () => {
    try {
      // 関連情報登録APIを呼び出し
      await insertEntityReferences(selectedSuggestions)

      success(t('message.entityReference.entityReferenceCreated'))

      // 初期化
      setSelectedSuggestions([])
      setRelatedSuggestions([])
      // 表示を関連情報テーブルに切り替え
      setDisplayState('relatedInfo')
    } catch (err) {
      showApiErrors(err)
    }
  }, [
    insertEntityReferences,
    selectedSuggestions,
    setDisplayState,
    setRelatedSuggestions,
    setSelectedSuggestions,
    showApiErrors,
    success,
    t,
  ])

  // 関連提案が表示されているときに「キャンセル」ボタンをクリックしたときの処理
  const handleCancelAddingRelatedInfoButtonClick = useCallback(() => {
    // 初期化
    setSelectedSuggestions([])
    setRelatedSuggestions([])
    // 表示を関連情報テーブルに切り替え
    setDisplayState('relatedInfo')
  }, [setSelectedSuggestions, setRelatedSuggestions, setDisplayState])

  return (
    <div className="flex justify-between items-center p-2.5">
      {/* 「関連情報を追加」ボタン */}
      {showSuggestionTable && (
        <div className="flex flex-row items-center gap-x-2.5 ml-auto">
          <CrewButton
            id="task-detail-related-info-list-toolbar-add-related-info-button"
            type="primary"
            text={t('action.addRelatedInformation')}
            onClick={handleAddRelatedInfoButtonClick}
            disabled={
              selectedSuggestions.length === 0 ||
              isLoadingInsertEntityReferencesMutation
            }
          />
          {/* 「キャンセル」ボタン */}
          <CrewButton
            id="task-detail-related-info-list-toolbar-cancel-adding-related-info-button"
            type="normal"
            stylingMode="outlined"
            text={t('action.cancel')}
            onClick={handleCancelAddingRelatedInfoButtonClick}
          />
        </div>
      )}
      {/* 「AIで関連情報を探す」ボタン */}
      {(showRelatedInfoTable || showSearching) && (
        <CrewButton
          id="task-detail-related-info-list-toolbar-find-related-info-button"
          type="primary"
          className="ml-auto"
          text={t('action.findRelatedInformationWithAI')}
          onClick={handleFindRelatedInfoButtonClick}
          disabled={showSearching || isLoadingGenerateRelatedMutation}
        />
      )}
    </div>
  )
})
